import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BookOpen, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import type { PageIssue, LowReadabilityDetails } from "@/types/technicalAudit";

interface LowReadabilityCardProps {
	issue: PageIssue;
}

function getReadabilityColor(score: number): { bar: string; text: string; level: string } {
	if (score >= 60) return { bar: "bg-green-500", text: "text-green-700 dark:text-green-400", level: "Easy" };
	if (score >= 30) return { bar: "bg-amber-500", text: "text-amber-700 dark:text-amber-400", level: "Moderate" };
	return { bar: "bg-red-500", text: "text-red-700 dark:text-red-400", level: "Difficult" };
}

export function LowReadabilityCard({ issue }: LowReadabilityCardProps) {
	const details = issue.issue_details as LowReadabilityDetails;
	const colors = getReadabilityColor(details.readability_score);
	const percentage = Math.min((details.readability_score / 100) * 100, 100);

	return (
		<div className="space-y-4">
			<div className="flex items-start gap-3 rounded-xl p-4 border bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800">
				<div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-amber-100 dark:bg-amber-900/40">
					<AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
				</div>
				<div className="flex-1">
					<h4 className="font-semibold text-amber-900 dark:text-amber-300">Low Readability</h4>
					<p className="text-sm mt-1 text-amber-700 dark:text-amber-400">
						This page has a readability score of {details.readability_score}, which is below the recommended minimum of {details.threshold}. Simpler content ranks better and engages more users.
					</p>
				</div>
			</div>

			<Card className="border-border shadow-sm">
				<CardHeader className="pb-3">
					<CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
						<div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center">
							<BookOpen className="w-4 h-4 text-blue-600 dark:text-blue-400" />
						</div>
						Readability Score
					</CardTitle>
				</CardHeader>
				<CardContent className="pt-0 space-y-4">
					<div className="flex items-center justify-between">
						<span className={cn("text-2xl font-bold", colors.text)}>
							{details.readability_score}
						</span>
						<span className="text-sm text-muted-foreground">
							Grade: {details.grade_level || colors.level}
						</span>
					</div>
					<div className="w-full h-3 bg-muted rounded-full overflow-hidden">
						<div
							className={cn("h-full rounded-full transition-all", colors.bar)}
							style={{ width: `${percentage}%` }}
						/>
					</div>
					<div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
						<span className="flex items-center gap-1">
							<span className="w-2 h-2 rounded-full bg-red-500" />
							&lt;30 Difficult
						</span>
						<span className="flex items-center gap-1">
							<span className="w-2 h-2 rounded-full bg-amber-500" />
							30-60 Moderate
						</span>
						<span className="flex items-center gap-1">
							<span className="w-2 h-2 rounded-full bg-green-500" />
							60+ Easy
						</span>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
